import { Link } from 'react-router-dom';

const footerServices = [
  { label: 'Hardwood Installation', href: '/services/installation' },
  { label: 'Floor Refinishing', href: '/services/refinishing' },
  { label: 'Floor Repair', href: '/services/repair' },
  { label: 'Floor Replacement', href: '/services/replacement' },
];

const footerLocations = [
  { label: 'Baltimore', href: '/locations/baltimore' },
  { label: 'Annapolis', href: '/locations/annapolis' },
  { label: 'Columbia', href: '/locations/columbia' },
  { label: 'Silver Spring', href: '/locations/silver-spring' },
  { label: 'Rockville', href: '/locations/rockville' },
];

const footerCompany = [
  { label: 'About Us', href: '/about' },
  { label: 'All Services', href: '/services' },
  { label: 'Service Areas', href: '/locations' },
];

interface FooterProps {
  onEstimateClick: () => void;
}

export default function Footer({ onEstimateClick }: FooterProps) {
  return (
    <footer className="bg-sand border-t border-sand-dark">
      {/* CTA Strip */}
      <div className="bg-walnut py-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-3">
            Ready to Transform Your Floors?
          </h2>
          <p className="text-white/70 font-sans text-base mb-8">
            Serving Maryland homeowners with precision craftsmanship and premium materials.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onEstimateClick}
              className="px-8 py-3.5 bg-gold text-walnut font-semibold rounded-lg hover:bg-gold-dark transition-colors whitespace-nowrap"
            >
              Get a Free Estimate
            </button>
            <a
              href="tel:+14105550192"
              className="px-8 py-3.5 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-phone-line" /> (410) 555-0192
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-5">
              <div className="h-14 w-auto flex items-center justify-center">
                <img
                  src="https://storage.readdy-site.link/project_files/e9d30abd-d6d8-4b84-ac41-23b773cb6a49/56a91e8e-7d83-4a3d-b7d3-a26defbf722a_premierlogo.png?v=d95b0f6312ed03f71c5c0454ebe11315"
                  alt="Premier Hardwood Floors Logo"
                  className="h-14 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-sm text-charcoal/70 font-sans leading-relaxed mb-5">
              Maryland's trusted hardwood flooring specialists. Precision craftsmanship, premium materials, transparent pricing.
            </p>
            <div className="flex gap-3">
              {['ri-facebook-fill', 'ri-instagram-line', 'ri-google-line', 'ri-yelp-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  rel="nofollow"
                  className="w-9 h-9 flex items-center justify-center bg-walnut/10 rounded-lg text-walnut hover:bg-walnut hover:text-white transition-all cursor-pointer"
                >
                  <i className={icon} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-sans text-xs uppercase tracking-widest text-oak font-semibold mb-5">
              <a href="/services" id="footer-services">Our Services</a>
            </h4>
            <ul className="space-y-3">
              {footerServices.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-charcoal/80 hover:text-oak transition-colors font-sans"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="font-sans text-xs uppercase tracking-widest text-oak font-semibold mb-5">
              <a href="/locations" id="footer-locations">Service Areas</a>
            </h4>
            <ul className="space-y-3">
              {footerLocations.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-charcoal/80 hover:text-oak transition-colors font-sans"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company + Contact */}
          <div>
            <h4 className="font-sans text-xs uppercase tracking-widest text-oak font-semibold mb-5">
              <a href="/about" id="footer-company">Company</a>
            </h4>
            <ul className="space-y-3 mb-6">
              {footerCompany.map((item) => (
                <li key={item.href}>
                  <Link
                    to={item.href}
                    className="text-sm text-charcoal/80 hover:text-oak transition-colors font-sans"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="space-y-2.5">
              <a href="tel:+14105550192" className="flex items-center gap-2.5 text-sm text-charcoal/80 hover:text-oak transition-colors font-sans">
                <i className="ri-phone-line text-oak" /> (410) 555-0192
              </a>
              <a href="mailto:info@premierhardwoodfloors.com" className="flex items-center gap-2.5 text-sm text-charcoal/80 hover:text-oak transition-colors font-sans">
                <i className="ri-mail-line text-oak" /> info@premierhardwood.com
              </a>
              <p className="flex items-start gap-2.5 text-sm text-charcoal/70 font-sans">
                <i className="ri-map-pin-line text-oak mt-0.5" /> Serving Maryland &amp; surrounding areas
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-sand-dark bg-walnut/5 px-6 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-charcoal/50 font-sans">
            &copy; 2026 Premier Hardwood Floors. All rights reserved. Maryland Licensed Contractor.
          </p>
          <div className="flex gap-5">
            <a href="#" rel="nofollow" className="text-xs text-charcoal/50 hover:text-oak transition-colors font-sans">Privacy Policy</a>
            <a href="#" rel="nofollow" className="text-xs text-charcoal/50 hover:text-oak transition-colors font-sans">Terms of Service</a>
            <a href="#" rel="nofollow" className="text-xs text-charcoal/50 hover:text-oak transition-colors font-sans">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
